import React, { PureComponent } from 'react';
import { Flex } from '../../common/Flex';
import { DelayByLocationSummary, DelaySummary } from 'app/model/SOL49';
import { LoadClassification, RefLocationType } from 'app/model/CORE';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { Col, Collapse, Row } from 'antd';

interface KeyIndicator {
    totalDelayMs: number;
    nbRegulatedFlights: number;
}

/* Compares average delay of two key indicators */
const AVG_DELAY_COMPARATOR_ASC = (a: KeyIndicator, b: KeyIndicator) => {
    const averageA = a.nbRegulatedFlights > 0 ? a.totalDelayMs / a.nbRegulatedFlights : 0;
    const averageB = b.nbRegulatedFlights > 0 ? b.totalDelayMs / b.nbRegulatedFlights : 0;
    return averageA - averageB;
};

const AVG_DELAY_COMPARATOR_DESC = (a: KeyIndicator, b: KeyIndicator) => {
    return -1 * AVG_DELAY_COMPARATOR_ASC(a, b);
};

interface KeyIndicatorsProps {
    delaySummary?: DelaySummary;
}

export class KeyIndicatorsV2 extends PureComponent<KeyIndicatorsProps> {
    renderAverageIndicator = (average: number) => {
        let text, backgroundColor, icon;
        if (average > 45) {
            text = "45'";
            backgroundColor = '#8C8C8C';
            icon = <CaretUpOutlined />;
        } else if (average > 30) {
            text = "30'";
            backgroundColor = '#5E5E5E';
            icon = <CaretUpOutlined />;
        } else if (average > 15) {
            text = "15'";
            backgroundColor = '#3B3b3B';
            icon = <CaretUpOutlined />;
        } else {
            text = "15'";
            backgroundColor = '#252525';
            icon = <CaretDownOutlined />;
        }
        return (
            <div>
                <div
                    style={{
                        backgroundColor: backgroundColor,
                        borderRadius: '0.4em',
                        display: 'inline-block',
                        padding: '0.15em',
                        color: 'white',
                    }}>
                    {icon}
                </div>
                <span style={{ marginLeft: '0.5em' }}>{text}</span>
            </div>
        );
    };

    render() {
        const delayByLocationSummary = this.props.delaySummary?.delayByLocationSummary;
        const delayByLoadClassification = this.props.delaySummary?.delayByLoadClassification;
        return (
            <Flex direction="vertical" className="key-indicators" grow={false}>
                <Collapse accordion={true} defaultActiveKey={'global'}>
                    <Collapse.Panel key={'global'} header={'Key indicators'}>
                        <Flex>
                            <Flex size="100%" style={{ margin: '1em auto', alignItems: 'center' }}>
                                <Flex direction="vertical" size="33%">
                                    <Flex
                                        style={{
                                            position: 'relative',
                                            color: '#9B9B9B',
                                            alignSelf: 'center',
                                            fontSize: '1.25em',
                                            fontWeight: 500,
                                        }}>
                                        {this.props.delaySummary
                                            ? (this.props.delaySummary.totalDelayMs / 60000).toFixed(0)
                                            : 0}
                                        <div style={{ fontSize: '0.75em', lineHeight: '1.25em' }}>min</div>
                                    </Flex>
                                    <Flex style={{ fontSize: '0.85em', alignSelf: 'center' }}>global delay</Flex>
                                </Flex>
                                <Flex direction="vertical" size="33%" style={{ lineHeight: '1.2em' }}>
                                    <Flex
                                        style={{
                                            position: 'relative',
                                            color: '#9B9B9B',
                                            alignSelf: 'center',
                                            fontSize: '1.25em',
                                            fontWeight: 500,
                                        }}>
                                        {delayByLoadClassification
                                            ? delayByLoadClassification
                                                  .filter(delayByLoadClassification => {
                                                      return (
                                                          delayByLoadClassification.loadClassification ===
                                                              LoadClassification.HIGH_THRESHOLD ||
                                                          delayByLoadClassification.loadClassification ===
                                                              LoadClassification.OVERLOAD
                                                      );
                                                  })
                                                  .map(
                                                      delayByLoadClassification =>
                                                          delayByLoadClassification.nbDelayedFlights,
                                                  )
                                                  .reduce((sum: number, nbFlights) => sum + nbFlights, 0)
                                            : 0}
                                    </Flex>
                                    <Flex style={{ fontSize: '0.85em', alignSelf: 'center' }}>
                                        flights delay <br />
                                        &gt; 30 min
                                    </Flex>
                                    <Flex
                                        style={{
                                            fontSize: '0.90em',
                                            alignSelf: 'center',
                                            color: 'gold',
                                            marginTop: '0.5em',
                                        }}>
                                        +0,0%
                                    </Flex>
                                </Flex>
                                <Flex direction="vertical" size="33%" style={{ lineHeight: '1.2em' }}>
                                    <Flex
                                        style={{
                                            position: 'relative',
                                            color: '#9B9B9B',
                                            alignSelf: 'center',
                                            fontSize: '1.25em',
                                            fontWeight: 500,
                                        }}>
                                        {this.props.delaySummary
                                            ? (this.props.delaySummary.averageDelayMs / 60000).toFixed(1)
                                            : 0}
                                        <div style={{ fontSize: '0.75em', lineHeight: '1.25em' }}>min</div>
                                    </Flex>
                                    <Flex style={{ fontSize: '0.85em', alignSelf: 'center' }}>average delay</Flex>
                                    <Flex
                                        style={{
                                            fontSize: '0.90em',
                                            alignSelf: 'center',
                                            color: 'gold',
                                            marginTop: '0.5em',
                                        }}>
                                        +0,0%
                                    </Flex>
                                </Flex>
                            </Flex>
                        </Flex>
                    </Collapse.Panel>
                    <Collapse.Panel key={'airspaces'} header={'Most delayed airspaces'}>
                        <Flex direction={'vertical'}>
                            <Row style={{ padding: '0.5em' }}>
                                <Col span={8}>airspaces</Col>
                                <Col span={8}>accumulated</Col>
                                <Col span={8}>average</Col>
                            </Row>
                            {this.renderDelayByLocationSummary(
                                delayByLocationSummary?.filter(
                                    summary => summary.refLocation.refType === RefLocationType.AS,
                                ),
                            )}
                        </Flex>
                    </Collapse.Panel>
                    <Collapse.Panel key={'airports'} header={'Most delayed airports'}>
                        <Flex direction={'vertical'}>
                            <Row style={{ padding: '0.5em' }}>
                                <Col span={8}>airports</Col>
                                <Col span={8}>accumulated</Col>
                                <Col span={8}>average</Col>
                            </Row>
                            {this.renderDelayByLocationSummary(
                                delayByLocationSummary?.filter(
                                    summary => summary.refLocation.refType === RefLocationType.AD,
                                ),
                            )}
                        </Flex>
                    </Collapse.Panel>
                </Collapse>
            </Flex>
        );
    }

    renderDelayByLocationSummary = (summary: DelayByLocationSummary[]) => {
        return summary
            ?.sort(AVG_DELAY_COMPARATOR_DESC)
            ?.slice(0, 5)
            ?.map((delaySummary, index) => {
                const totalDelayMn = delaySummary.totalDelayMs > 0 ? delaySummary.totalDelayMs / 60000 : 0;
                const averageMn = totalDelayMn / delaySummary.nbRegulatedFlights;
                return (
                    <Row key={index} style={{ padding: '0.5em' }} className={'indicator'}>
                        <Col span={10}>{delaySummary.displayText || delaySummary.refLocation.refId}</Col>
                        <Col span={6}>{totalDelayMn.toFixed(0) + "'"}</Col>
                        <Col span={8}>{this.renderAverageIndicator(averageMn)}</Col>
                    </Row>
                );
            });
    };
}
